/**
 * SecurityInvestigation — Modal/panel component that displays the results of a
 * security investigation for an employee. Shows sign-in analysis, CrowdStrike
 * detections, and risk assessment with color-coded severity.
 *
 * Called from EmployeeDetail page via a "Security Audit" button.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// ─── Types ─────────────────────────────────────────────────────────────

interface SignInEntry {
  date: string;
  ip: string;
  city: string;
  country: string;
  app: string;
  isKnown: boolean;
  deviceManaged: boolean;
  deviceCompliant: boolean;
  deviceName: string;
  matchesCrowdStrikeHost: boolean;
  crowdStrikeHostname?: string;
  os: string;
  browser: string;
  trustType: string;
}

interface IPEnrichmentEntry {
  ip: string;
  city: string;
  region: string;
  country: string;
  isp: string;
  org: string;
  as: string;
  proxy: boolean;
  hosting: boolean;
  mobile: boolean;
  lat: number;
  lon: number;
}

interface InvestigationData {
  user: { email: string; displayName: string };
  period: { days: number; from: string; to: string };
  signIns: {
    available: boolean;
    note?: string;
    total: number;
    suspicious: SignInEntry[];
    caBlocked?: Array<{
      date: string;
      ip: string;
      city: string;
      country: string;
      app: string;
      failureReason: string;
      os: string;
      browser: string;
      deviceName: string;
    }>;
    byLocation: Array<{ city: string; country: string; count: number }>;
    byApp: Array<{ app: string; count: number }>;
  };
  knownIPs: string[];
  ipEnrichment?: IPEnrichmentEntry[];
  crowdstrike: {
    hosts: Array<{
      hostname: string;
      platform: string;
      lastSeen: string;
      externalIp: string;
      status: string;
    }>;
    detections: Array<{
      id: string;
      severity: string;
      description: string;
      date: string;
      hostname: string;
    }>;
    incidents: Array<{
      id: string;
      name: string;
      description: string;
      score: number;
      start: string;
    }>;
  };
  riskSummary: {
    level: 'low' | 'medium' | 'high' | 'critical';
    factors: string[];
  };
  aiAnalysis: string | null;
  exchange: {
    available: boolean;
    note?: string;
    inboxRules: Array<{ name: string; enabled: boolean; forwardTo?: string; redirectTo?: string; deleteMessage?: boolean }>;
    recentSentCount: number;
  };
  graphData: {
    directoryAudits: {
      available: boolean;
      note?: string;
      events: Array<{ date: string; activity: string; target: string; initiatedBy: string; result: string }>;
    };
    authMethods: {
      available: boolean;
      note?: string;
      methods: Array<{ type: string; id: string }>;
    };
    oauthConsents: {
      available: boolean;
      note?: string;
      apps: Array<{ appName: string; scope: string; consentDate: string }>;
    };
    recentFiles: {
      available: boolean;
      note?: string;
      files: Array<{ name: string; lastModified: string; webUrl: string }>;
    };
    intuneDevices: {
      available: boolean;
      note?: string;
      devices: Array<{ name: string; compliance: string; lastSync: string; os: string }>;
    };
    mailboxSettings: {
      available: boolean;
      note?: string;
      forwardingAddress?: string;
      autoForwardEnabled?: boolean;
      delegateAccess?: string[];
    };
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────

const RISK_COLORS: Record<string, { bg: string; text: string; border: string; label: string }> = {
  low: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Low Risk' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Medium Risk' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'High Risk' },
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Critical Risk' },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

// ─── Component ─────────────────────────────────────────────────────────

interface SecurityInvestigationProps {
  email: string;
  onClose: () => void;
}

export function SecurityInvestigation({ email, onClose }: SecurityInvestigationProps) {
  const [days, setDays] = useState(14);

  const { data, isLoading, error, refetch } = useQuery<InvestigationData>({
    queryKey: ['security-investigation', email, days],
    queryFn: async () => {
      // Start investigation via POST — returns jobId immediately
      const startResp = await apiClient.post<InvestigationData | { jobId: string; status: string }>('security-investigation/start', { email, days });
      
      // If we got full results back (server completed instantly), return them
      if ('user' in startResp) return startResp as InvestigationData;
      
      // Poll for results — the first poll triggers actual execution on the server
      const jobId = (startResp as { jobId: string }).jobId;
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000)); // Poll every 2 seconds
        try {
          const result = await apiClient.get<InvestigationData | { jobId: string; status: string; error?: string }>('security-investigation/result', { jobId });
          if ('user' in result) return result as InvestigationData;
          if ((result as { status: string }).status === 'failed') {
            const errMsg = (result as { error?: string }).error ?? 'Investigation failed on the server';
            throw new Error(errMsg);
          }
        } catch (pollErr) {
          // If poll itself fails (network timeout from SWA proxy), keep trying
          // The server is still working — it just took longer than 30s
          if (i > 80) throw pollErr; // Give up after ~3 minutes
        }
      }
      throw new Error('Investigation timed out — please try again');
    },
    enabled: !!email,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/40">
      <div className="bg-white rounded-lg border border-border shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Security Investigation</h2>
            <p className="text-sm text-slate-500 mt-0.5">{email}</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border border-border rounded px-2 py-1"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={() => refetch()}
              className="text-sm text-baker-red hover:underline"
            >
              Refresh
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading && <LoadingSpinner message="Running security investigation…" />}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              <strong>Error:</strong> {(error as Error).message}
            </div>
          )}

          {data && <InvestigationResults data={data} />}
        </div>
      </div>
    </div>
  );
}

// ─── Results Display ───────────────────────────────────────────────────

function InvestigationResults({ data }: { data: InvestigationData }) {
  const risk = RISK_COLORS[data.riskSummary.level] ?? RISK_COLORS.low;

  return (
    <>
      {/* Risk Summary */}
      <section className={`rounded-lg border ${risk.border} ${risk.bg} p-5`}>
        <div className="flex items-center gap-3 mb-2">
          <span className={`text-base font-semibold ${risk.text}`}>{risk.label}</span>
          <span className="text-sm text-slate-500">
            {data.period.days}-day investigation period ({new Date(data.period.from).toLocaleDateString()} – {new Date(data.period.to).toLocaleDateString()})
          </span>
        </div>
        {data.riskSummary.factors.length > 0 ? (
          <ul className="list-disc list-inside text-sm space-y-1 mt-2">
            {data.riskSummary.factors.map((f, i) => (
              <li key={i} className={risk.text}>{f}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-green-600">No suspicious activity detected.</p>
        )}
      </section>

      {/* AI Analysis */}
      {data.aiAnalysis && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">🤖 AI Security Analysis</h3>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{data.aiAnalysis}</div>
        </section>
      )}

      {/* Exchange Mailbox */}
      <section className="bg-white rounded-lg border border-border p-5">
        <h3 className="text-base font-semibold text-text-primary mb-3">📧 Exchange Mailbox</h3>
        {!data.exchange?.available ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
            {data.exchange?.note ?? 'Exchange data not available.'}
          </div>
        ) : (
          <div>
            {data.exchange.inboxRules.length > 0 ? (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Inbox Rules ({data.exchange.inboxRules.length})</h4>
                <div className="space-y-2">
                  {data.exchange.inboxRules.map((rule, i) => (
                    <div key={i} className={`p-2 rounded border text-xs ${rule.forwardTo || rule.redirectTo || rule.deleteMessage ? 'border-red-200 bg-red-50' : 'border-border bg-slate-50'}`}>
                      <span className="font-medium">{rule.name}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                        {rule.enabled ? 'Active' : 'Disabled'}
                      </span>
                      {rule.forwardTo && <span className="ml-2 text-red-600">⚠ Forwards to: {rule.forwardTo}</span>}
                      {rule.redirectTo && <span className="ml-2 text-red-600">⚠ Redirects to: {rule.redirectTo}</span>}
                      {rule.deleteMessage && <span className="ml-2 text-red-600">⚠ Deletes message</span>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-green-600 mb-3">No inbox rules configured.</p>
            )}
            {data.exchange.recentSentCount > 0 && (
              <p className="text-xs text-slate-500">Recent sent emails: {data.exchange.recentSentCount}</p>
            )}
          </div>
        )}
      </section>

      {/* Mailbox Transport Settings (forwarding at transport level) */}
      {data.graphData?.mailboxSettings && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">📬 Mail Forwarding &amp; Delegates</h3>
          {!data.graphData.mailboxSettings.available ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
              {data.graphData.mailboxSettings.note ?? 'Mailbox settings not available.'}
            </div>
          ) : (
            <div className="space-y-2">
              {data.graphData.mailboxSettings.autoForwardEnabled ? (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  <strong>⚠ Transport-Level Forwarding Active:</strong> {data.graphData.mailboxSettings.forwardingAddress}
                  <p className="text-xs mt-1 text-red-600">This is not visible in inbox rules — mail is being forwarded at the server level.</p>
                </div>
              ) : (
                <p className="text-sm text-green-600">No transport-level forwarding configured.</p>
              )}
              {data.graphData.mailboxSettings.delegateAccess && data.graphData.mailboxSettings.delegateAccess.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
                  <strong>Delegate Access:</strong> {data.graphData.mailboxSettings.delegateAccess.join(', ')}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Directory Audit Logs */}
      {data.graphData?.directoryAudits && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">📋 Directory Audit Logs</h3>
          {!data.graphData.directoryAudits.available ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
              {data.graphData.directoryAudits.note ?? 'Directory audit logs not available.'}
            </div>
          ) : data.graphData.directoryAudits.events.length === 0 ? (
            <p className="text-sm text-green-600">No directory changes found for this user in the investigation period.</p>
          ) : (
            <div className="overflow-auto max-h-64 border border-border rounded">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Activity</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Initiated By</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {data.graphData.directoryAudits.events.map((evt, i) => {
                    const isSuspicious = ['Reset user password', 'Update user', 'Consent to application', 'Add member to role'].some(a => evt.activity.includes(a));
                    return (
                      <tr key={i} className={`border-b border-border-subtle ${isSuspicious ? 'bg-red-50/50' : ''}`}>
                        <td className="py-1.5 px-3 text-slate-600 whitespace-nowrap">{formatDate(evt.date)}</td>
                        <td className="py-1.5 px-3">
                          {isSuspicious && <span className="text-red-500 mr-1">⚠</span>}
                          {evt.activity}
                        </td>
                        <td className="py-1.5 px-3 text-xs">{evt.initiatedBy}</td>
                        <td className="py-1.5 px-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            evt.result === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {evt.result}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Authentication Methods (MFA) */}
      {data.graphData?.authMethods && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">🔐 Registered Auth Methods (MFA)</h3>
          {!data.graphData.authMethods.available ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
              {data.graphData.authMethods.note ?? 'Auth methods not available.'}
            </div>
          ) : data.graphData.authMethods.methods.length === 0 ? (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              ⚠ No MFA methods registered — account may be at risk.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.graphData.authMethods.methods.map((m, i) => (
                <span key={i} className="inline-flex items-center px-3 py-1.5 rounded border border-border bg-slate-50 text-xs">
                  <span className="font-medium">{m.type}</span>
                  <span className="ml-2 text-slate-400 text-[10px]">{m.id.substring(0, 8)}…</span>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* OAuth App Consents */}
      {data.graphData?.oauthConsents && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">🔗 OAuth App Consents</h3>
          {!data.graphData.oauthConsents.available ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
              {data.graphData.oauthConsents.note ?? 'OAuth consent data not available.'}
            </div>
          ) : data.graphData.oauthConsents.apps.length === 0 ? (
            <p className="text-sm text-green-600">No third-party app consents found.</p>
          ) : (
            <div className="overflow-auto max-h-48 border border-border rounded">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">App / Client ID</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Scope</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Consent Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.graphData.oauthConsents.apps.map((app, i) => (
                    <tr key={i} className="border-b border-border-subtle">
                      <td className="py-1.5 px-3 font-medium">{app.appName}</td>
                      <td className="py-1.5 px-3 text-xs font-mono">{app.scope || '—'}</td>
                      <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">{app.consentDate ? formatDate(app.consentDate) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Recent Files (SharePoint/OneDrive) */}
      {data.graphData?.recentFiles && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">📁 Recent File Activity (OneDrive/SharePoint)</h3>
          {!data.graphData.recentFiles.available ? (
            <div className={`rounded p-3 text-sm ${
              data.graphData.recentFiles.note?.includes('not provisioned') || data.graphData.recentFiles.note?.includes('404')
                ? 'bg-slate-50 border border-border text-slate-500'
                : 'bg-amber-50 border border-amber-200 text-amber-700'
            }`}>
              {data.graphData.recentFiles.note?.includes('not provisioned') || data.graphData.recentFiles.note?.includes('404')
                ? 'User does not have OneDrive/SharePoint provisioned — file activity monitoring not applicable.'
                : (data.graphData.recentFiles.note ?? 'File activity not available.')}
            </div>
          ) : data.graphData.recentFiles.files.length === 0 ? (
            <p className="text-sm text-green-600">No recent file activity found.</p>
          ) : (
            <div>
              {data.graphData.recentFiles.files.length >= 40 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700 mb-3">
                  ⚠ High volume of recent file access ({data.graphData.recentFiles.files.length} files) — review for potential data exfiltration.
                </div>
              )}
              <div className="overflow-auto max-h-48 border border-border rounded">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">File Name</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Last Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.graphData.recentFiles.files.map((f, i) => (
                      <tr key={i} className="border-b border-border-subtle">
                        <td className="py-1.5 px-3">
                          {f.webUrl ? (
                            <a href={f.webUrl} target="_blank" rel="noopener noreferrer" className="text-baker-red hover:underline">{f.name}</a>
                          ) : (
                            f.name
                          )}
                        </td>
                        <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">{formatDate(f.lastModified)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Intune Device Compliance */}
      {data.graphData?.intuneDevices && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">💻 Intune Device Compliance</h3>
          {!data.graphData.intuneDevices.available ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
              {data.graphData.intuneDevices.note ?? 'Intune device data not available.'}
            </div>
          ) : data.graphData.intuneDevices.devices.length === 0 ? (
            <p className="text-sm text-slate-400">No managed devices found for this user.</p>
          ) : (
            <div className="overflow-auto max-h-48 border border-border rounded">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Device</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">OS</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Compliance</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Last Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {data.graphData.intuneDevices.devices.map((d, i) => (
                    <tr key={i} className="border-b border-border-subtle">
                      <td className="py-1.5 px-3 font-medium">{d.name}</td>
                      <td className="py-1.5 px-3">{d.os}</td>
                      <td className="py-1.5 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          d.compliance === 'compliant' ? 'bg-green-100 text-green-700' :
                          d.compliance === 'noncompliant' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {d.compliance}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">{formatDate(d.lastSync)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Sign-In Analysis */}
      <section className="bg-white rounded-lg border border-border p-5">
        <h3 className="text-base font-semibold text-text-primary mb-3">
          Sign-In Activity
          {data.signIns.available && (
            <span className="text-sm font-normal text-slate-500 ml-2">
              ({data.signIns.total} total sign-ins)
            </span>
          )}
        </h3>

        {!data.signIns.available && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700 mb-4">
            {data.signIns.note ?? 'Sign-in logs are not available.'}
          </div>
        )}

        {/* CRITICAL: Conditional Access Blocked Sign-Ins (valid password, policy denied) */}
        {data.signIns.available && data.signIns.caBlocked && data.signIns.caBlocked.length > 0 && (
          <div className="mb-4">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
              <h4 className="text-sm font-bold text-red-800 mb-1 flex items-center gap-2">
                🚨 Conditional Access Blocked — Valid Credentials Detected ({data.signIns.caBlocked.length})
              </h4>
              <p className="text-xs text-red-700 mb-3">
                These sign-in attempts had the correct password but were blocked by Conditional Access policy.
                This means the attacker has valid credentials. Immediate password reset and session revocation required.
              </p>
              <div className="overflow-auto max-h-48 border border-red-200 rounded">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-red-100 z-10">
                    <tr className="border-b-2 border-red-300">
                      <th className="text-left py-2 px-3 font-medium text-red-800">Date</th>
                      <th className="text-left py-2 px-3 font-medium text-red-800">IP Address</th>
                      <th className="text-left py-2 px-3 font-medium text-red-800">Location</th>
                      <th className="text-left py-2 px-3 font-medium text-red-800">Application</th>
                      <th className="text-left py-2 px-3 font-medium text-red-800">Block Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.signIns.caBlocked.map((s, i) => (
                      <tr key={i} className="border-b border-red-200 bg-red-50/50">
                        <td className="py-1.5 px-3 text-red-700 whitespace-nowrap">{formatDate(s.date)}</td>
                        <td className="py-1.5 px-3 font-mono text-xs text-red-800">{s.ip}</td>
                        <td className="py-1.5 px-3">{s.city}, {s.country}</td>
                        <td className="py-1.5 px-3">{s.app}</td>
                        <td className="py-1.5 px-3 text-xs text-red-700">{s.failureReason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {data.signIns.available && data.signIns.suspicious.length > 0 && (
          <div className="mb-4">
            {(() => {
              const trulyUnknown = data.signIns.suspicious.filter(s => !s.deviceManaged && !s.matchesCrowdStrikeHost);
              const verifiedDevice = data.signIns.suspicious.filter(s => s.deviceManaged || s.matchesCrowdStrikeHost);
              return (
                <>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">
                    Sign-Ins from Non-Office IPs ({data.signIns.suspicious.length} total
                    {trulyUnknown.length > 0 && <span className="text-red-600 ml-1">— {trulyUnknown.length} unverified</span>}
                    {verifiedDevice.length > 0 && <span className="text-green-600 ml-1">— {verifiedDevice.length} on verified devices</span>})
                  </h4>
                </>
              );
            })()}
            <div className="overflow-auto max-h-64 border border-border rounded">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">IP Address</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Location</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Device / OS</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Application</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.signIns.suspicious.map((s, i) => (
                    <tr key={i} className={`border-b border-border-subtle hover:bg-red-50/30 ${
                      s.deviceManaged || s.matchesCrowdStrikeHost ? 'bg-green-50/30' : ''
                    }`}>
                      <td className="py-1.5 px-3 text-slate-600 whitespace-nowrap">{formatDate(s.date)}</td>
                      <td className="py-1.5 px-3 font-mono text-xs">{s.ip}</td>
                      <td className="py-1.5 px-3">{s.city}, {s.country}</td>
                      <td className="py-1.5 px-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs">{s.os || 'Unknown OS'}{s.browser && s.browser !== 'Unknown' ? ` / ${s.browser}` : ''}</span>
                          {s.deviceName && <span className="text-[10px] text-slate-400">{s.deviceName}</span>}
                          <div className="flex gap-1 flex-wrap">
                            {s.deviceManaged && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Managed</span>
                            )}
                            {s.deviceCompliant && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Compliant</span>
                            )}
                            {s.matchesCrowdStrikeHost && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700" title={`CrowdStrike: ${s.crowdStrikeHostname}`}>CS Match</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-1.5 px-3">{s.app}</td>
                      <td className="py-1.5 px-3">
                        {s.deviceManaged || s.matchesCrowdStrikeHost ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            Verified Device
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            Unknown IP
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.signIns.available && data.signIns.suspicious.length === 0 && (
          <p className="text-sm text-green-600 mb-4">All sign-in IPs match known Meraki locations.</p>
        )}

        {/* Location breakdown */}
        {data.signIns.available && data.signIns.byLocation.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">By Location</h4>
              <div className="overflow-auto max-h-40 border border-border rounded">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 px-3 font-medium text-slate-600">City</th>
                      <th className="text-left py-1.5 px-3 font-medium text-slate-600">Country</th>
                      <th className="text-right py-1.5 px-3 font-medium text-slate-600">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.signIns.byLocation.map((loc, i) => (
                      <tr key={i} className="border-b border-border-subtle">
                        <td className="py-1 px-3">{loc.city}</td>
                        <td className="py-1 px-3">{loc.country}</td>
                        <td className="py-1 px-3 text-right font-medium">{loc.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">By Application</h4>
              <div className="overflow-auto max-h-40 border border-border rounded">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 px-3 font-medium text-slate-600">App</th>
                      <th className="text-right py-1.5 px-3 font-medium text-slate-600">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.signIns.byApp.map((a, i) => (
                      <tr key={i} className="border-b border-border-subtle">
                        <td className="py-1 px-3">{a.app}</td>
                        <td className="py-1 px-3 text-right font-medium">{a.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* IP Intelligence — Enrichment for Unknown IPs */}
      {data.ipEnrichment && data.ipEnrichment.length > 0 && (
        <section className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-text-primary mb-3">
            🌐 IP Intelligence
            <span className="text-sm font-normal text-slate-500 ml-2">
              ({data.ipEnrichment.length} unknown IPs enriched)
            </span>
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Geolocation, ISP, and threat indicators for IPs not associated with Baker offices or managed devices.
          </p>
          <div className="overflow-auto max-h-64 border border-border rounded">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-3 font-medium text-slate-600">IP Address</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Location</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">ISP</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Organization</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">ASN</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.ipEnrichment.map((ip, i) => (
                  <tr key={i} className={`border-b border-border-subtle ${
                    ip.proxy || ip.hosting ? 'bg-red-50/40' : ''
                  }`}>
                    <td className="py-1.5 px-3 font-mono text-xs">{ip.ip}</td>
                    <td className="py-1.5 px-3">
                      <div className="flex flex-col">
                        <span>{ip.city}, {ip.region}</span>
                        <span className="text-[10px] text-slate-400">{ip.country}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-3 text-xs">{ip.isp}</td>
                    <td className="py-1.5 px-3 text-xs">{ip.org}</td>
                    <td className="py-1.5 px-3 text-xs font-mono">{ip.as}</td>
                    <td className="py-1.5 px-3">
                      <div className="flex gap-1 flex-wrap">
                        {ip.proxy && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                            VPN/Proxy
                          </span>
                        )}
                        {ip.hosting && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
                            Datacenter
                          </span>
                        )}
                        {ip.mobile && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                            Mobile
                          </span>
                        )}
                        {!ip.proxy && !ip.hosting && !ip.mobile && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                            Residential
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.ipEnrichment.some(ip => ip.proxy || ip.hosting) && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
              ⚠ IPs flagged as VPN/Proxy or Datacenter are commonly used to anonymize traffic. This may indicate an attacker masking their true location, or a user with a personal VPN.
            </div>
          )}
        </section>
      )}

      {/* CrowdStrike Section */}
      <section className="bg-white rounded-lg border border-border p-5">
        <h3 className="text-base font-semibold text-text-primary mb-3">CrowdStrike Falcon</h3>

        {/* Hosts */}
        {data.crowdstrike.hosts.length > 0 ? (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-slate-700 mb-2">
              Registered Hosts ({data.crowdstrike.hosts.length})
            </h4>
            <div className="overflow-auto max-h-40 border border-border rounded">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Hostname</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Platform</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">External IP</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.crowdstrike.hosts.map((h, i) => (
                    <tr key={i} className="border-b border-border-subtle">
                      <td className="py-1.5 px-3 font-medium">{h.hostname}</td>
                      <td className="py-1.5 px-3">{h.platform}</td>
                      <td className="py-1.5 px-3 font-mono text-xs">{h.externalIp}</td>
                      <td className="py-1.5 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          h.status === 'normal' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {h.status}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">{formatDate(h.lastSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 mb-4">No CrowdStrike hosts found for this user.</p>
        )}

        {/* Detections */}
        {data.crowdstrike.detections.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-red-700 mb-2">
              ⚠ Detections ({data.crowdstrike.detections.length})
            </h4>
            <div className="overflow-auto max-h-48 border border-red-200 rounded">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-red-50 z-10">
                  <tr className="border-b-2 border-red-200">
                    <th className="text-left py-2 px-3 font-medium text-red-700">Severity</th>
                    <th className="text-left py-2 px-3 font-medium text-red-700">Description</th>
                    <th className="text-left py-2 px-3 font-medium text-red-700">Host</th>
                    <th className="text-left py-2 px-3 font-medium text-red-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.crowdstrike.detections.map((d, i) => (
                    <tr key={i} className="border-b border-red-100 hover:bg-red-50/50">
                      <td className="py-1.5 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          d.severity === 'Critical' ? 'bg-red-200 text-red-800' :
                          d.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                          d.severity === 'Medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {d.severity}
                        </span>
                      </td>
                      <td className="py-1.5 px-3">{d.description}</td>
                      <td className="py-1.5 px-3 font-medium">{d.hostname}</td>
                      <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">{formatDate(d.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Incidents */}
        {data.crowdstrike.incidents.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-red-700 mb-2">
              🚨 Incidents ({data.crowdstrike.incidents.length})
            </h4>
            <div className="space-y-2">
              {data.crowdstrike.incidents.map((inc, i) => (
                <div key={i} className="bg-red-50 border border-red-200 rounded p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-red-800">{inc.name}</span>
                    <span className="text-xs text-red-600">Score: {inc.score}</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">{inc.description}</p>
                  <p className="text-xs text-slate-500 mt-1">Started: {formatDate(inc.start)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.crowdstrike.detections.length === 0 && data.crowdstrike.incidents.length === 0 && data.crowdstrike.hosts.length > 0 && (
          <p className="text-sm text-green-600">No detections or incidents found for this user's hosts.</p>
        )}
      </section>

      {/* Known IPs Reference */}
      <section className="bg-white rounded-lg border border-border p-5">
        <h3 className="text-base font-semibold text-text-primary mb-3">
          Known Meraki IPs ({data.knownIPs.length})
        </h3>
        <div className="overflow-auto max-h-32">
          <div className="flex flex-wrap gap-1.5">
            {data.knownIPs.map((ip) => (
              <span key={ip} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-600 border border-border-subtle">
                {ip}
              </span>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
